import { Typography, Paper } from '@mui/material';
import { useSaveContext } from 'common/Context';
import { appLoadingAnimAtom } from 'utils/state';
import { useStateXAtom } from '@cloudio-saas/statex';
import { useAppID } from 'designer/atoms';
import { makeStyles } from '@mui/styles';

const loaderOptions = [
  { id: 'spinner', label: 'Spinner' },
  { id: 'dots', label: 'Bouncing Dots' },
  { id: 'bars', label: 'Loading Bars' },
  { id: 'dotted-spinner', label: 'Default' },
];

export const useStylesLoader = makeStyles({
  loaderPreview: {
    marginTop: 20,
    height: 80,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  loaderSpinner: {
    border: '7px solid #e0e0e0',
    borderTop: '7px solid #1976d2',
    borderRadius: '50%',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    animation: '$spin 1s linear infinite',
  },

  loaderDots: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    position: 'relative',

    '& div, &::before, &::after': {
      content: '""',
      width: 10,
      height: 10,
      background: '#1976d2',
      borderRadius: '50%',
      animation: '$bounce 1.4s infinite ease-in-out',
      display: 'block',
      marginRight: '3px',
    },

    '&::before': {
      left: '0%',
      animationDelay: '-0.32s',
    },

    '& div': {
      left: '50%',
      transform: 'translateX(-50%)',
      animationDelay: '-0.16s',
    },

    '&::after': {
      right: '0%',
    },
  },

  loaderBars: {
    width: 40,
    height: 40,
    position: 'relative',

    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      width: 6,
      height: '100%',
      background: '#1976d2',
      animation: '$stretch 1.2s infinite ease-in-out',
    },

    '&::before': {
      left: 8,
      animationDelay: '-0.2s',
    },

    '&::after': {
      right: 8,
      animationDelay: '-0.4s',
    },
  },

  loaderRing: {
    position: 'relative',
    width: '100%',
    height: '100%',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      border: '7px solid #1976d2',
      borderColor: '#1976d2 transparent #1976d2 transparent',
      boxSizing: 'border-box',
      animation: '$dualRing 1.2s linear infinite',
    },
  },

  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },

  '@keyframes bounce': {
    '0%, 80%, 100%': { transform: 'scale(0)' },
    '40%': { transform: 'scale(1)' },
  },

  '@keyframes stretch': {
    '0%, 40%, 100%': { transform: 'scaleY(0.4)' },
    '20%': { transform: 'scaleY(1)' },
  },

  '@keyframes dualRing': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  loader: {
    height: 40,
    width: 40,
    position: 'relative',
    zIndex: 99999,
  },
  loaderItem: {
    position: 'absolute',
    width: 5,
    height: 5,
    borderRadius: '50%',
    animation: '$loader 2s linear infinite',
  },
  a: {
    left: 18,
  },
  b: {
    right: 5,
    top: 5,
    animationDelay: '0.2s',
  },
  c: {
    top: 18,
    right: 0,
    animationDelay: '0.4s',
  },
  d: {
    right: 5,
    bottom: 5,
    animationDelay: '0.6s',
  },
  e: {
    left: 18,
    bottom: 0,
    animationDelay: '0.8s',
  },
  f: {
    left: 5,
    bottom: 5,
    animationDelay: '1s',
  },
  g: {
    top: 18,
    left: 0,
    animationDelay: '1.2s',
  },
  h: {
    left: 5,
    top: 5,
    animationDelay: '1.4s',
  },
  '@keyframes loader': {
    '0%': {
      background: '#fff',
    },
    '100%': {
      background: '#000',
    },
  },
});

export default function MaskLoaderSettings() {
  const { setContextValue } = useSaveContext();
  const appUid = useAppID();
  const [selected, setSelected] = useStateXAtom(appLoadingAnimAtom, {
    params: { appUid },
  });
  const classes = useStylesLoader();

  return (
    <div>
      <Typography
        gutterBottom
        sx={{
          fontSize: '0.8rem',
          color: 'rgba(0, 0, 0, 0.6)',
          marginTop: '2px',
        }}
      >
        Page-Mask Loading Animation:
      </Typography>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {loaderOptions.map((option) => {
          const tip = {
            'data-tip': `${option.label}`,
          };
          return (
            <div {...tip}>
              <Paper
                key={option.id}
                elevation={selected === option.id ? 4 : 1}
                sx={{
                  border:
                    selected === option.id
                      ? '2px solid #1976d2'
                      : '1px solid #ccc',
                  borderRadius: 2,
                  cursor: 'pointer',
                  width: 80,
                  height: 80,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  setSelected(option.id);
                  setContextValue(
                    appLoadingAnimAtom.path[4] as string,
                    option.id,
                    appLoadingAnimAtom.path[1] as string,
                    [appLoadingAnimAtom.path[4]],
                    'appUid',
                    appUid,
                  );
                }}
              >
                <div style={{ height: '40px', width: '40px' }}>
                  {option.id !== 'dotted-spinner' ? (
                    <div
                      className={
                        option.id === 'spinner'
                          ? classes.loaderSpinner
                          : option.id === 'dots'
                          ? classes.loaderDots
                          : option.id === 'bars'
                          ? classes.loaderBars
                          : ''
                      }
                    >
                      {(option.id === 'dots' || option.id === 'bars') && (
                        <div></div>
                      )}
                    </div>
                  ) : (
                    <div id="loader">
                      <div className="loader-item a"></div>
                      <div className="loader-item b"></div>
                      <div className="loader-item c"></div>
                      <div className="loader-item d"></div>
                      <div className="loader-item e"></div>
                      <div className="loader-item f"></div>
                      <div className="loader-item g"></div>
                      <div className="loader-item h"></div>
                    </div>
                  )}
                </div>
              </Paper>
            </div>
          );
        })}
      </div>
    </div>
  );
}
